import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Divider } from '@mui/material';
import './AlgorithmSelector.css';

const AlgorithmSelector = ({ algorithms, selectedAlgorithms, onChange, type }) => {
  if (!algorithms) return <div>Invalid mode selected</div>;

  const handleSelectionChange = (event) => {
    const newValue = event.target.value;
    const previousValue = selectedAlgorithms;
    
    // Find what changed by comparing the arrays
    const clickedValue = newValue.length < previousValue.length
      ? previousValue.find(id => !newValue.includes(id))  // Item was removed
      : newValue.find(id => !previousValue.includes(id)); // Item was added
    
    // If no value was changed, return
    if (!clickedValue) return;

    let newSelected = [...selectedAlgorithms];

    // Handle special cases
    if (clickedValue === "ALL") {
      newSelected = newSelected.length === Object.keys(algorithms.cases).length 
        ? [] 
        : Object.keys(algorithms.cases);
    } 
    // Handle type selection
    else if (typeof clickedValue === 'string' && clickedValue.startsWith("TYPE_")) {
      const selectedType = clickedValue.replace("TYPE_", "");
      const algsOfType = Object.values(algorithms.cases)
        .filter(alg => alg.type === selectedType)
        .map(alg => alg.id);
      
      const allTypeSelected = algsOfType.every(id => selectedAlgorithms.includes(id));
      
      if (allTypeSelected) {
        newSelected = selectedAlgorithms.filter(id => !algsOfType.includes(id));
      } else {
        newSelected = [...new Set([...selectedAlgorithms, ...algsOfType])];
      }
    }
    // Handle individual algorithm selection
    else {
      if (selectedAlgorithms.includes(clickedValue)) {
        newSelected = selectedAlgorithms.filter(id => id !== clickedValue);
      } else {
        newSelected = [...selectedAlgorithms, clickedValue];
      }
    }

    onChange(newSelected);
  };

  // Group cases by type
  const casesByType = {};
  Object.values(algorithms.cases).forEach(alg => {
    const type = alg.type || 'Other';
    if (!casesByType[type]) {
      casesByType[type] = [];
    }
    casesByType[type].push(alg);
  });

  // Create individual cases menu items
  const renderIndividualCases = () => {
    return Object.entries(casesByType).reduce((acc, [typeGroup, cases]) => {
      return [
        ...acc,
        <MenuItem key={`header-${typeGroup}`} disabled style={{ paddingLeft: '16px' }}>
          <ListItemText primary={typeGroup} />
        </MenuItem>,
        ...cases.map(alg => (
          <MenuItem 
            key={alg.id} 
            value={alg.id}
            style={{ paddingLeft: '48px' }}
          >
            <Checkbox checked={selectedAlgorithms.includes(alg.id)} />
            <ListItemText primary={alg.name} />
          </MenuItem>
        ))
      ];
    }, []);
  };

  return (
    <div className="algorithm-selector-container">
      <FormControl fullWidth>
        <InputLabel>{`Select ${type} Cases`}</InputLabel>
        <Select
          multiple
          value={selectedAlgorithms}
          onChange={handleSelectionChange}
          renderValue={(selected) => `${selected.length} cases selected`}
          label={`Select ${type} Cases`}
        >
          {/* All Cases Option */}
          <MenuItem value="ALL">
            <Checkbox 
              checked={selectedAlgorithms.length === Object.keys(algorithms.cases).length}
              indeterminate={selectedAlgorithms.length > 0 && 
                selectedAlgorithms.length < Object.keys(algorithms.cases).length}
            />
            <ListItemText primary="All Cases" />
          </MenuItem>

          <Divider />
          
          {/* Type Selection Section */}
          <MenuItem disabled>
            <ListItemText 
              primary="Select by Type" 
              style={{ fontWeight: 'bold' }}
            />
          </MenuItem>

          {Object.entries(casesByType).map(([typeGroup, cases]) => (
            <MenuItem 
              key={`TYPE_${typeGroup}`} 
              value={`TYPE_${typeGroup}`}
              className="type-header"
              style={{ paddingLeft: '32px' }}
            >
              <Checkbox 
                checked={cases.every(alg => selectedAlgorithms.includes(alg.id))}
                indeterminate={
                  cases.some(alg => selectedAlgorithms.includes(alg.id)) &&
                  !cases.every(alg => selectedAlgorithms.includes(alg.id))
                }
              />
              <ListItemText primary={`${typeGroup}`} />
            </MenuItem>
          ))}

          <Divider />

          {/* Individual Cases Section */}
          <MenuItem disabled>
            <ListItemText 
              primary="Select by Case" 
              style={{ fontWeight: 'bold' }}
            />
          </MenuItem>

          {renderIndividualCases()}
        </Select>
      </FormControl>
    </div>
  );
};

export default AlgorithmSelector; 